import * as Zod from "zod";
import { never } from "~/lib/never";

export const tokensDecoder = Zod.object({
  id_token: Zod.string(),
  access_token: Zod.string(),
});

export type MarketplaceTokens = Zod.infer<typeof tokensDecoder>;

export const edgeLambdaTokenDecoder = Zod.object({
  userId: Zod.string(),
  organizationId: Zod.string(),
});

export type EdgeLambdaToken = Zod.infer<typeof edgeLambdaTokenDecoder>;

// Cookie Identity (Vendr Platform) ----------------------------

export const cookieIdentityClaimsDecoder = Zod.object({
  organizationId: Zod.string(),
  organizationName: Zod.string().nullish(),
  userId: Zod.string().optional().nullable(),
  personId: Zod.string().optional().nullable(),
  domain: Zod.string(),
  userEmail: Zod.string(),
  name: Zod.string(),
  role: Zod.string().optional().nullable(),
  triggersAnalytics: Zod.boolean().optional().nullable(),
  isImpersonationSession: Zod.boolean().optional().nullable(),
  impersonationBy: Zod.string().optional().nullable(),
  trackingId: Zod.string().nullish(),
  profileImage: Zod.string().nullish(),
  backofficeCompanyId: Zod.string().optional().nullable(),
});

export type CookieIdentityClaims = Zod.infer<
  typeof cookieIdentityClaimsDecoder
>;

// API Identity ------------------------------------------------

// Organization

const commonOrganizationDecoder = Zod.object({
  name: Zod.string(),
});

const baseOrganizationDecoder = Zod.object({
  id: Zod.string(),
  domain: Zod.string(),
});

const platformOrganizationDecoder = baseOrganizationDecoder.extend({
  triggersAnalytics: Zod.boolean().optional().nullable(),
  backofficeCompanyId: Zod.string().optional().nullable(),
});

const clerkOrganizationDecoder = baseOrganizationDecoder.extend({});

// User

const commonUserDecoder = Zod.object({
  email: Zod.string(),
  name: Zod.string(),
  role: Zod.string().optional().nullable(),
  isImpersonationSession: Zod.boolean().optional().nullable(),
  impersonationBy: Zod.string().optional().nullable(),
  trackingId: Zod.string().nullish(),
  profileImage: Zod.string().nullish(),
});

const clerkUserDecoder = Zod.object({
  id: Zod.string(),
});

const platformUserDecoder = Zod.object({
  id: Zod.string().optional().nullable(),
  personId: Zod.string().optional().nullable(),
});

// Identity Claims

const platformIdentityClaimsDecoder = Zod.object({
  type: Zod.literal("platform"),
  organization: commonOrganizationDecoder.merge(platformOrganizationDecoder),
  user: commonUserDecoder.merge(platformUserDecoder),
});

const clerkIdentityClaimsDecoder = Zod.object({
  type: Zod.literal("clerk"),
  organization: commonOrganizationDecoder.extend({
    clerk: clerkOrganizationDecoder,
    platform: platformOrganizationDecoder.nullish(),
  }),
  user: commonUserDecoder.extend({
    clerk: clerkUserDecoder,
    platform: platformUserDecoder.nullish(),
  }),
});

export const v2IdentityClaimsDecoder = Zod.union([
  platformIdentityClaimsDecoder,
  clerkIdentityClaimsDecoder,
]);

export type ClerkIdentityClaims = Zod.infer<typeof clerkIdentityClaimsDecoder>;

// Original IdentityClaimsDecoder before Clerk

export const v1IdentityClaimsDecoder = Zod.object({
  // Original v1 Schema
  organizationId: Zod.string(),
  organizationName: Zod.string().nullish(),
  userId: Zod.string(),
  personId: Zod.string().optional().nullable(),
  domain: Zod.string(),
  userEmail: Zod.string(),
  name: Zod.string(),
  role: Zod.string().optional().nullable(),
  triggersAnalytics: Zod.boolean().optional().nullable(),
  isImpersonationSession: Zod.boolean().optional().nullable(),
  impersonationBy: Zod.string().optional().nullable(),
  trackingId: Zod.string().nullish(),
  profileImage: Zod.string().nullish(),

  // New explicit identity origin fields
  clerkUserId: Zod.string().optional().nullable(),
  clerkOrganizationId: Zod.string().optional().nullable(),
  izUserId: Zod.string().optional().nullable(),
  izOrganizationId: Zod.string().optional().nullable(),

  // Backoffice company ID
  backofficeCompanyId: Zod.string().optional().nullable(),
});

export type IdentityClaims = Zod.infer<typeof v1IdentityClaimsDecoder>;
export type IdentityClaimsV2 = Zod.infer<typeof v2IdentityClaimsDecoder>;

export type MarketplaceIdentityClaims = IdentityClaimsV2 & {
  // Internal Marketplace org/user ids
  organizationId: string;
  userId: string;
  ip: string | null;
};

// Clerk Auth

export const clerkSessionClaimsDecoder = Zod.object({
  IZOrganizationId: Zod.string().nullish(),
  IZOrganizationDomain: Zod.string().nullish(),
  IZPersonId: Zod.string().nullish(),
  IZUserId: Zod.string().nullish(),
  ClerkOrgName: Zod.string().nullish(),
  ClerkUserPrimaryEmail: Zod.string(),
  ClerkUserFullname: Zod.string().nullish(),
  ClerkUserImageUrl: Zod.string().nullish(),
  IZTriggersAnalytics: Zod.boolean().nullish(),
  BackofficeCompanyId: Zod.string().nullish(),
});

export const clerkAuthDecoder = Zod.object({
  userId: Zod.string(),
  orgId: Zod.string(),
  orgRole: Zod.string(),
  sessionClaims: clerkSessionClaimsDecoder,
  actor: Zod.object({
    sub: Zod.string(),
  }).optional(),
});

export function hasOptedIntoPlatform(
  identity: IdentityClaimsV2,
  platformOptedInCookie: string | null,
) {
  if (!isMarketplaceUser(identity)) {
    return true;
  }

  if (
    platformOptedInCookie &&
    platformOptedInCookie === IdentityClaimsUtil.userId(identity)
  ) {
    return true;
  }

  return false;
}

export function isMarketplaceUser(identity: IdentityClaimsV2) {
  const role = IdentityClaimsUtil.role(identity);

  return (
    role === "marketplace_user" || // IZ role
    role === "org:marketplace_user" || // Clerk role
    !role // role not set default to marketplace_user as IZ is being phased out
  );
}

/**
 * Utility class for working with IdentityClaims.
 *
 * Operations mainly prefer IZ values over Clerk values.
 *
 * This helper class will go away once Clerk auth is fully enabled.
 */
export class IdentityClaimsUtil {
  /**
   * Converts a v2 IdentityClaims to a v1 IdentityClaims to minimize breaking changes.
   */
  public static toV1IdentityClaims(
    identity: IdentityClaimsV2 | null,
  ): IdentityClaims | null {
    if (!identity) {
      return null;
    }

    const userId = this.userId(identity);
    if (!userId) {
      return null;
    }

    const clerkOrganizationId =
      identity.type === "clerk" ? identity.organization.clerk.id : null;

    const clerkUserId =
      identity.type === "clerk" ? identity.user.clerk.id : null;

    const izOrganizationId =
      identity.type === "platform"
        ? identity.organization.id
        : identity.organization.platform?.id;

    const izUserId =
      identity.type === "platform"
        ? identity.user.id
        : identity.user.platform?.id;

    const backofficeCompanyId =
      identity.type === "platform"
        ? identity.organization.backofficeCompanyId
        : identity.organization.platform?.backofficeCompanyId;

    return {
      // Original v1 Schema
      organizationId: this.organizationId(identity),
      organizationName: identity.organization.name,
      userId,
      personId: this.personId(identity),
      domain: this.domain(identity),
      userEmail: identity.user.email,
      name: identity.user.name,
      role: this.role(identity),
      triggersAnalytics: this.triggersAnalytics(identity),
      isImpersonationSession: identity.user.isImpersonationSession,
      impersonationBy: identity.user.impersonationBy,
      trackingId: identity.user.trackingId,
      profileImage: identity.user.profileImage,

      // New explicit identity origin fields
      clerkOrganizationId,
      clerkUserId,
      izOrganizationId,
      izUserId,

      // Backoffice company ID
      backofficeCompanyId,
    };
  }

  public static organizationId(identity: IdentityClaimsV2) {
    switch (identity.type) {
      case "platform":
        return identity.organization.id;
      case "clerk":
        return (
          identity.organization.platform?.id ?? identity.organization.clerk.id
        );
      default:
        return never(identity);
    }
  }

  public static userId(identity: IdentityClaimsV2) {
    switch (identity.type) {
      case "platform":
        return identity.user.id;
      case "clerk":
        return identity.user.platform?.id ?? identity.user.clerk.id;
      default:
        return never(identity);
    }
  }

  public static personId(identity: IdentityClaimsV2) {
    switch (identity.type) {
      case "platform":
        return identity.user.personId;
      case "clerk":
        return identity.user.platform?.personId ?? null;
      default:
        return never(identity);
    }
  }

  public static domain(identity: IdentityClaimsV2) {
    switch (identity.type) {
      case "platform":
        return identity.organization.domain;
      case "clerk":
        return (
          identity.organization.platform?.domain ??
          identity.organization.clerk.domain
        );
      default:
        return never(identity);
    }
  }

  public static triggersAnalytics(identity: IdentityClaimsV2) {
    switch (identity.type) {
      case "platform":
        return identity.organization.triggersAnalytics;
      case "clerk":
        return identity.organization.platform?.triggersAnalytics ?? false;
    }
  }

  public static role(identity: IdentityClaimsV2) {
    return this.commonUserFields(identity).role;
  }

  public static isImpersonationSession(identity: IdentityClaimsV2) {
    return this.commonUserFields(identity).isImpersonationSession ?? false;
  }

  public static email(identity: IdentityClaimsV2) {
    return this.commonUserFields(identity).email;
  }

  private static commonUserFields(identity: IdentityClaimsV2) {
    switch (identity.type) {
      case "platform":
      case "clerk":
        return identity.user;
      default:
        return never(identity);
    }
  }
}
